import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Truck, Phone, Mail, MapPin, Shield, Clock, Award, ChevronRight,
  Package, Container, Weight, ArrowRight, Menu, X, Star
} from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_dispatch-pro-75/artifacts/edmtbm97_26023.jpg";
const HERO_IMG = "https://customer-assets-7cd3h4nn.emergentagent.net/job_0fa017c3-1a8c-446d-bf7a-2bb1123504ca/artifacts/q8zl7bfl_d0990c11e7a035ae1ba2f0991e87fbcce7c9bcfd4d51a8eec4222de9de148027.png";
const FLEET_IMG = "https://customer-assets.emergentagent.com/job_dispatch-pro-75/artifacts/ao3jvzic_24144.jpg";
const OVERSIZE_IMG = "https://customer-assets.emergentagent.com/job_dispatch-pro-75/artifacts/vzys4u8x_24292.jpg";
const FLATBED_IMG = "https://customer-assets.emergentagent.com/job_dispatch-pro-75/artifacts/jnji3nbv_22845.jpg";
const SIDELIFTER_IMG = "https://customer-assets.emergentagent.com/job_dispatch-pro-75/artifacts/n1skp82m_59467.jpg";
const OVERSIZE_BLUE_IMG = "https://customer-assets.emergentagent.com/job_dispatch-pro-75/artifacts/g4kkv8u7_59465.jpg";
const OVERSIZE_CRATE_IMG = "https://customer-assets.emergentagent.com/job_dispatch-pro-75/artifacts/jknifyn9_59463.jpg";
const FREMANTLE_JOB_IMG = "https://customer-assets-7cd3h4nn.emergentagent.net/job_0fa017c3-1a8c-446d-bf7a-2bb1123504ca/artifacts/kblpm14y_20260818_151445.webp";
const SKEL_TRAILER_IMG = "https://customer-assets-7cd3h4nn.emergentagent.net/job_0fa017c3-1a8c-446d-bf7a-2bb1123504ca/artifacts/csao5sg0_20260821_095247.webp";

const services = [
  { title: "General Freight", desc: "Reliable door-to-door freight services across Australia. From palletised goods to full truck loads, we handle it all.", icon: Package },
  { title: "Oversize Loads", desc: "Specialist oversize and over-dimensional transport with all required permits, pilot vehicles, and route planning.", icon: Weight },
  { title: "Container Transport", desc: "Full container cartage services including port pick-up, delivery, and empty container returns. 20ft and 40ft.", icon: Container },
  { title: "Sidelifter Operations", desc: "Self-loading sidelifter container transport for locations without crane access. Anywhere, anytime.", icon: Package },
  { title: "Interstate Freight", desc: "Cross-state transport solutions connecting major cities and regional areas across Australia.", icon: Truck },
  { title: "Construction Haulage", desc: "Heavy machinery and construction material transport. Excavators, loaders, steel, concrete.", icon: Shield },
  { title: "Machinery Transport", desc: "Safe and secure transport of industrial machinery and equipment with specialised techniques.", icon: Award },
];

const fleetTypes = [
  { name: "Flat Beds", desc: "Versatile open-top trailers for steel, timber, machinery, and oversized cargo.", img: FLATBED_IMG },
  { name: "Drop Deck", desc: "Low-profile trailers for tall loads and heavy machinery with extra height clearance.", img: OVERSIZE_BLUE_IMG },
  { name: "Tautliner", desc: "Curtain-sided trailers for weather-protected freight with quick side-loading access.", img: FLEET_IMG },
  { name: "Skel Trailers", desc: "Skeletal trailers purpose-built for 20ft and 40ft container transport.", img: SKEL_TRAILER_IMG },
  { name: "Sidelifter", desc: "Self-loading container trailers with onboard cranes. No external crane required.", img: SIDELIFTER_IMG },
];

// ─── Scroll Reveal Hook ───
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ─── Parallax Hook ───
function useParallax(speed = 0.3) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const scrolled = window.innerHeight - rect.top;
      setOffset(scrolled * speed);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [speed]);
  return [ref, offset];
}

// ─── Animated Counter ───
function AnimatedStat({ value, label, visible, delay }) {
  const [count, setCount] = useState(0);
  const num = parseInt(value) || 0;
  useEffect(() => {
    if (!visible || !num) return;
    const timeout = setTimeout(() => {
      let start = 0;
      const step = Math.max(1, Math.ceil(num / 40));
      const timer = setInterval(() => {
        start += step;
        if (start >= num) { setCount(num); clearInterval(timer); }
        else setCount(start);
      }, 25);
      return () => clearInterval(timer);
    }, delay);
    return () => clearTimeout(timeout);
  }, [visible, num, delay]);

  const display = num === 0 ? value : `${count}${value.replace(/\d+/, "")}`;
  return (
    <div className="text-center" style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)", transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${delay}ms` }}>
      <p className="font-heading text-3xl sm:text-5xl font-black text-orange-500">{display}</p>
      <p className="text-xs text-zinc-500 uppercase tracking-wider mt-2">{label}</p>
    </div>
  );
}

export default function WebsitePage() {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", company: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [heroScale, setHeroScale] = useState(1);
  const [heroOpacity, setHeroOpacity] = useState(1);
  const contactRef = useRef(null);
  const servicesRef = useRef(null);
  const fleetRef = useRef(null);
  const aboutRef = useRef(null);

  // Scroll-driven hero zoom
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setNavScrolled(y > 50);
      // Hero zoom: scale from 1 to 1.15 as you scroll first 600px
      const scale = 1 + Math.min(y / 4000, 0.15);
      setHeroScale(scale);
      // Hero text fades out
      const opacity = Math.max(0, 1 - y / 500);
      setHeroOpacity(opacity);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [statsRef, statsVisible] = useReveal(0.3);
  const [aboutRef2, aboutVisible] = useReveal(0.15);
  const [servicesRef2, servicesVisible] = useReveal(0.1);
  const [fleetRef2, fleetVisible] = useReveal(0.1);
  const [galleryRef, galleryVisible] = useReveal(0.1);
  const [whyRef, whyVisible] = useReveal(0.1);
  const [contactRef2, contactVisible] = useReveal(0.1);
  const [aboutImgRef, aboutImgOffset] = useParallax(0.08);

  const scrollTo = (ref) => { setMobileMenu(false); ref.current?.scrollIntoView({ behavior: "smooth" }); };

  const handleContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.message) { toast.error("Name and message are required"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Message sent! We'll get back to you shortly.");
      setContactForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch { toast.error("Failed to send. Please call us directly."); }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white overflow-x-hidden" data-testid="website-page">

      {/* ─── NAVBAR ─── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navScrolled ? "bg-[#09090B]/95 backdrop-blur-lg border-b border-zinc-800/60 shadow-2xl shadow-black/30" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="SGHK" className="w-10 h-10 object-contain" />
            <div>
              <span className="font-heading text-lg font-bold tracking-tight text-white">SGHK LOGISTICS</span>
              <p className="text-[9px] text-zinc-500 uppercase tracking-[0.2em] -mt-0.5">Transport & Haulage</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo(aboutRef)} className="text-sm text-zinc-400 hover:text-white transition-colors relative group" data-testid="nav-about">About<span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-orange-500 group-hover:w-full transition-all duration-300" /></button>
            <button onClick={() => scrollTo(servicesRef)} className="text-sm text-zinc-400 hover:text-white transition-colors relative group" data-testid="nav-services">Services<span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-orange-500 group-hover:w-full transition-all duration-300" /></button>
            <button onClick={() => scrollTo(fleetRef)} className="text-sm text-zinc-400 hover:text-white transition-colors relative group" data-testid="nav-fleet">Fleet<span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-orange-500 group-hover:w-full transition-all duration-300" /></button>
            <button onClick={() => scrollTo(contactRef)} className="text-sm text-zinc-400 hover:text-white transition-colors relative group" data-testid="nav-contact">Contact<span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-orange-500 group-hover:w-full transition-all duration-300" /></button>
            <Link to="/login" className="text-xs text-zinc-500 hover:text-zinc-300 border border-zinc-700 px-3 py-1.5 rounded-sm hover:border-zinc-500 transition-all" data-testid="nav-team-login">Team Login</Link>
            <button onClick={() => scrollTo(contactRef)} className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider px-4 py-2 rounded-[2px] transition-all hover:shadow-lg hover:shadow-orange-500/20 active:scale-95" data-testid="nav-get-quote">Get a Quote</button>
          </div>
          <button className="md:hidden text-zinc-300" onClick={() => setMobileMenu(!mobileMenu)} data-testid="mobile-menu-toggle">{mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
        </div>
        {mobileMenu && (
          <div className="md:hidden bg-[#0f0f12] border-t border-zinc-800 px-6 py-4 space-y-3" style={{animation: "slideInUp 0.3s ease-out"}}>
            <button onClick={() => scrollTo(aboutRef)} className="block text-sm text-zinc-300 w-full text-left py-1">About</button>
            <button onClick={() => scrollTo(servicesRef)} className="block text-sm text-zinc-300 w-full text-left py-1">Services</button>
            <button onClick={() => scrollTo(fleetRef)} className="block text-sm text-zinc-300 w-full text-left py-1">Fleet</button>
            <button onClick={() => scrollTo(contactRef)} className="block text-sm text-zinc-300 w-full text-left py-1">Contact</button>
            <Link to="/login" className="block text-sm text-zinc-500 py-1">Team Login</Link>
            <button onClick={() => scrollTo(contactRef)} className="bg-orange-500 text-white text-xs font-medium uppercase tracking-wider px-4 py-2 rounded-[2px] w-full mt-2">Get a Quote</button>
          </div>
        )}
      </nav>

      {/* ─── HERO (Scroll-driven zoom) ─── */}
      <section className="relative h-screen flex items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0" style={{ transform: `scale(${heroScale})`, transition: "transform 0.1s linear" }}>
          <img src={HERO_IMG} alt="SGHK Logistics - Heavy Haulage and Transport Services in Perth, Western Australia" className="w-full h-full object-cover object-top" />
          {/* Only a subtle bottom gradient for button readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-transparent to-transparent" style={{opacity: 0.85}} />
        </div>
        {/* SEO H1 - visually hidden but accessible to search engines */}
        <h1 className="sr-only">SGHK Logistics - Heavy Haulage & Transport Services in Perth, Western Australia</h1>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 pb-16" style={{ opacity: heroOpacity, transform: `translateY(${(1 - heroOpacity) * -20}px)`, transition: "opacity 0.05s, transform 0.05s" }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 mb-4 border border-white/15 bg-black/30 backdrop-blur-sm px-4 py-1.5 rounded-full" style={{animation: "fadeIn 0.8s ease-out 0.5s both"}}>
                <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                <span className="text-orange-400 text-xs font-medium uppercase tracking-[0.2em]">Australia-Wide Transport</span>
              </div>
              <p className="text-white/80 text-base sm:text-lg max-w-md leading-relaxed font-body" style={{animation: "slideInUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.7s both"}}>
                Heavy haulage, oversize loads, container work & general freight. <span className="text-orange-400 font-medium">Delivered right, every time.</span>
              </p>
            </div>
            <div className="flex flex-wrap gap-3" style={{animation: "slideInUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.9s both"}}>
              <button onClick={() => scrollTo(contactRef)} className="bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm uppercase tracking-wider px-8 py-3.5 rounded-[2px] flex items-center gap-2 transition-all hover:shadow-xl hover:shadow-orange-500/25 active:scale-95" data-testid="hero-get-quote">
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </button>
              <a href="tel:0414337803" className="border border-white/20 hover:border-white/40 backdrop-blur-sm bg-white/5 text-white font-medium text-sm px-8 py-3.5 rounded-[2px] flex items-center gap-2 transition-all hover:bg-white/10" data-testid="hero-call-now">
                <Phone className="w-4 h-4" /> Call Now
              </a>
            </div>
          </div>
        </div>
        {/* Scroll indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-10" style={{animation: "fadeIn 1s ease-out 1.5s both"}}>
          <span className="text-[9px] text-white/40 uppercase tracking-widest">Scroll</span>
          <div className="w-[1px] h-6 bg-gradient-to-b from-white/40 to-transparent animate-pulse" />
        </div>
      </section>

      {/* ─── STATS BAR ─── */}
      <section ref={statsRef} className="bg-[#0f0f12] border-y border-zinc-800/60 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 via-transparent to-orange-500/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 relative">
          {[
            { value: "10+", label: "Years Experience" },
            { value: "500+", label: "Loads Delivered" },
            { value: "24/7", label: "Availability" },
            { value: "100%", label: "Safety Record" },
          ].map((s, i) => (
            <AnimatedStat key={s.label} value={s.value} label={s.label} visible={statsVisible} delay={i * 150} />
          ))}
        </div>
      </section>

      {/* ─── ABOUT ─── */}
      <section id="about" ref={(el) => { aboutRef.current = el; aboutRef2.current = el; }} className="py-24 sm:py-32" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div style={{ opacity: aboutVisible ? 1 : 0, transform: aboutVisible ? "translateX(0)" : "translateX(-50px)", transition: "all 0.8s cubic-bezier(0.16,1,0.3,1)" }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-[2px] bg-orange-500" />
                <span className="text-orange-500 text-xs font-medium uppercase tracking-[0.25em]">About Us</span>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-black text-white tracking-tight mb-6">
                Trusted Transport Partner Across Australia
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                SGHK Logistics is a professional transport and haulage company providing reliable freight solutions across Australia. With years of hands-on experience, we understand the importance of getting your goods where they need to be — safely and on time.
              </p>
              <p className="text-zinc-400 leading-relaxed mb-8">
                Our modern fleet of well-maintained vehicles and experienced drivers means we can handle everything from standard freight to oversize loads and specialist container work.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Shield, text: "Fully Insured & Licensed" },
                  { icon: Clock, text: "24/7 Availability" },
                  { icon: Award, text: "Safety Accredited" },
                  { icon: Truck, text: "Modern Fleet" },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.text} className="flex items-center gap-2.5 hover-lift" style={{ opacity: aboutVisible ? 1 : 0, transform: aboutVisible ? "translateY(0)" : "translateY(20px)", transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${400 + i * 100}ms` }}>
                      <div className="w-9 h-9 rounded-sm bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-orange-500" />
                      </div>
                      <span className="text-sm text-zinc-300">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div ref={aboutImgRef} className="relative" style={{ opacity: aboutVisible ? 1 : 0, transform: aboutVisible ? `translateX(0) translateY(${-aboutImgOffset}px)` : "translateX(50px)", transition: "opacity 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s" }}>
              <img src={OVERSIZE_IMG} alt="SGHK Oversize Transport" className="w-full rounded-sm border border-zinc-800 hover:border-orange-500/30 transition-all duration-500" />
              <div className="absolute -bottom-5 -left-5 bg-orange-500 text-white p-5 rounded-sm hidden sm:block shadow-xl shadow-orange-500/20" style={{animation: aboutVisible ? "scaleIn 0.5s cubic-bezier(0.16,1,0.3,1) 0.8s both" : "none"}}>
                <p className="font-heading text-3xl font-black">10+</p>
                <p className="text-xs uppercase tracking-wider">Years in Business</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SERVICES ─── */}
      <section id="services" ref={(el) => { servicesRef.current = el; servicesRef2.current = el; }} className="py-24 sm:py-32 bg-[#0f0f12]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16" style={{ opacity: servicesVisible ? 1 : 0, transform: servicesVisible ? "translateY(0)" : "translateY(30px)", transition: "all 0.7s cubic-bezier(0.16,1,0.3,1)" }}>
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-8 h-[2px] bg-orange-500" />
              <span className="text-orange-500 text-xs font-medium uppercase tracking-[0.25em]">What We Do</span>
              <div className="w-8 h-[2px] bg-orange-500" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black text-white tracking-tight">Our Services</h2>
            <p className="text-zinc-500 mt-3 max-w-lg mx-auto text-sm">Comprehensive transport solutions for every need.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((svc, i) => {
              const Icon = svc.icon;
              return (
                <div key={svc.title}
                  className="border border-zinc-800 bg-[#18181B] rounded-sm p-6 hover-lift hover-glow group cursor-default"
                  data-testid={`service-card-${i}`}
                  style={{ opacity: servicesVisible ? 1 : 0, transform: servicesVisible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.95)", transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${i * 80}ms` }}>
                  <div className="w-12 h-12 rounded-sm bg-orange-500/10 flex items-center justify-center mb-4 group-hover:bg-orange-500/20 group-hover:scale-110 transition-all duration-300">
                    <Icon className="w-5 h-5 text-orange-500" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">{svc.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{svc.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FLEET ─── */}
      <section id="fleet" ref={(el) => { fleetRef.current = el; fleetRef2.current = el; }} className="py-24 sm:py-32" data-testid="fleet-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16" style={{ opacity: fleetVisible ? 1 : 0, transform: fleetVisible ? "translateY(0)" : "translateY(30px)", transition: "all 0.7s cubic-bezier(0.16,1,0.3,1)" }}>
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-8 h-[2px] bg-orange-500" />
              <span className="text-orange-500 text-xs font-medium uppercase tracking-[0.25em]">Our Equipment</span>
              <div className="w-8 h-[2px] bg-orange-500" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black text-white tracking-tight">Our Fleet</h2>
            <p className="text-zinc-500 mt-3 max-w-lg mx-auto text-sm">Modern, well-maintained vehicles ready for any job.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {fleetTypes.map((ft, i) => (
              <div key={ft.name}
                className="relative overflow-hidden rounded-sm border border-zinc-800 group hover-glow cursor-default"
                data-testid={`fleet-card-${i}`}
                style={{ opacity: fleetVisible ? 1 : 0, transform: fleetVisible ? "translateY(0)" : "translateY(40px)", transition: `all 0.7s cubic-bezier(0.16,1,0.3,1) ${i * 100}ms` }}>
                <div className="overflow-hidden">
                  <img src={ft.img} alt={ft.name} className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-700 ease-out" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 transform group-hover:translate-y-0 transition-transform">
                  <h3 className="font-heading text-xl font-bold text-white mb-1">{ft.name}</h3>
                  <p className="text-sm text-zinc-300 leading-relaxed">{ft.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── GALLERY ─── */}
      <section ref={galleryRef} className="py-24 sm:py-32 bg-[#0f0f12]" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16" style={{ opacity: galleryVisible ? 1 : 0, transform: galleryVisible ? "translateY(0)" : "translateY(30px)", transition: "all 0.7s cubic-bezier(0.16,1,0.3,1)" }}>
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-8 h-[2px] bg-orange-500" />
              <span className="text-orange-500 text-xs font-medium uppercase tracking-[0.25em]">On the Road</span>
              <div className="w-8 h-[2px] bg-orange-500" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black text-white tracking-tight">Our Work</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Fremantle Port Job - Featured */}
            <div className="md:col-span-2 overflow-hidden rounded-sm border border-zinc-800 relative group hover-glow"
              style={{ opacity: galleryVisible ? 1 : 0, transform: galleryVisible ? "scale(1)" : "scale(0.9)", transition: "all 0.6s cubic-bezier(0.16,1,0.3,1)" }}>
              <img src={FREMANTLE_JOB_IMG} alt="Fremantle Berth 11 & 12 - Cable Reel Transport" className="w-full h-72 md:h-80 object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#09090B]/90 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-heading text-xl font-bold text-white mb-1">Fremantle Port - Berth 11 & 12</h3>
                <p className="text-sm text-zinc-300">Heavy cable reel transport from vessel to site</p>
              </div>
            </div>
            <div className="overflow-hidden rounded-sm border border-zinc-800 group hover-glow"
              style={{ opacity: galleryVisible ? 1 : 0, transform: galleryVisible ? "scale(1)" : "scale(0.9)", transition: "all 0.6s cubic-bezier(0.16,1,0.3,1) 100ms" }}>
              <img src={SIDELIFTER_IMG} alt="Sidelifter" className="w-full h-72 md:h-80 object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="overflow-hidden rounded-sm border border-zinc-800 group hover-glow"
              style={{ opacity: galleryVisible ? 1 : 0, transform: galleryVisible ? "scale(1)" : "scale(0.9)", transition: "all 0.6s cubic-bezier(0.16,1,0.3,1) 200ms" }}>
              <img src={OVERSIZE_CRATE_IMG} alt="Crate Transport" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="md:col-span-2 overflow-hidden rounded-sm border border-zinc-800 relative group"
              style={{ opacity: galleryVisible ? 1 : 0, transform: galleryVisible ? "scale(1)" : "scale(0.9)", transition: "all 0.6s cubic-bezier(0.16,1,0.3,1) 300ms" }}>
              <img src={OVERSIZE_IMG} alt="Heavy Haulage" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#09090B]/80 to-transparent flex items-center">
                <div className="p-8">
                  <h3 className="font-heading text-2xl font-black text-white mb-2">Ready for Your Next Load?</h3>
                  <button onClick={() => scrollTo(contactRef)} className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider px-5 py-2.5 rounded-[2px] flex items-center gap-2 transition-all hover:shadow-lg hover:shadow-orange-500/20 mt-3 active:scale-95">
                    Contact Us <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHY CHOOSE US ─── */}
      <section ref={whyRef} className="py-24 sm:py-32" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4" style={{ opacity: whyVisible ? 1 : 0, transform: whyVisible ? "translateX(0)" : "translateX(-30px)", transition: "all 0.6s cubic-bezier(0.16,1,0.3,1)" }}>
                <div className="w-8 h-[2px] bg-orange-500" />
                <span className="text-orange-500 text-xs font-medium uppercase tracking-[0.25em]">Why SGHK</span>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-black text-white tracking-tight mb-8" style={{ opacity: whyVisible ? 1 : 0, transform: whyVisible ? "translateX(0)" : "translateX(-30px)", transition: "all 0.6s cubic-bezier(0.16,1,0.3,1) 100ms" }}>Why Choose Us</h2>
              <div className="space-y-5">
                {[
                  { title: "Competitive Pricing", desc: "Fair and transparent pricing. No hidden costs, no surprises." },
                  { title: "On-Time Delivery", desc: "We plan every route for maximum efficiency and reliability." },
                  { title: "Experienced Team", desc: "Highly experienced drivers for oversize, dangerous goods, and heavy machinery." },
                  { title: "Modern Equipment", desc: "Well-maintained Volvo prime movers and a range of trailers for every job." },
                  { title: "Personal Service", desc: "You deal directly with us. Hardy is always available to discuss your transport needs." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 hover-lift rounded-sm p-2 -ml-2"
                    style={{ opacity: whyVisible ? 1 : 0, transform: whyVisible ? "translateX(0)" : "translateX(-30px)", transition: `all 0.5s cubic-bezier(0.16,1,0.3,1) ${200 + i * 80}ms` }}>
                    <div className="w-8 h-8 rounded-sm bg-orange-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <ChevronRight className="w-4 h-4 text-orange-500" />
                    </div>
                    <div>
                      <h4 className="font-heading text-sm font-bold text-white mb-1">{item.title}</h4>
                      <p className="text-xs text-zinc-400 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              {[
                { text: "SGHK Logistics handled our oversize machinery transport flawlessly. Professional team, great communication.", from: "Construction Industry Client", delay: 0 },
                { text: "Reliable, on-time, and competitive rates. We've been using SGHK for our container work and couldn't be happier.", from: "Import/Export Business", delay: 100 },
                { text: "Great blokes to deal with. Always available when we need last-minute freight moved. Highly recommend.", from: "Regional Freight Client", delay: 200 },
              ].map((t, i) => (
                <div key={i} className="border border-zinc-800 bg-[#18181B] rounded-sm p-6 hover-lift hover-glow"
                  style={{ opacity: whyVisible ? 1 : 0, transform: whyVisible ? "translateX(0)" : "translateX(30px)", transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${t.delay + 200}ms` }}>
                  <div className="flex gap-1 mb-3">{[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 text-orange-500 fill-orange-500" />)}</div>
                  <p className="text-sm text-zinc-300 leading-relaxed mb-3">"{t.text}"</p>
                  <p className="text-xs text-zinc-500 font-medium">- {t.from}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section id="contact" ref={(el) => { contactRef.current = el; contactRef2.current = el; }} className="py-24 sm:py-32 bg-[#0f0f12]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16" style={{ opacity: contactVisible ? 1 : 0, transform: contactVisible ? "translateY(0)" : "translateY(30px)", transition: "all 0.7s cubic-bezier(0.16,1,0.3,1)" }}>
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-8 h-[2px] bg-orange-500" />
              <span className="text-orange-500 text-xs font-medium uppercase tracking-[0.25em]">Get In Touch</span>
              <div className="w-8 h-[2px] bg-orange-500" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black text-white tracking-tight">Contact Us</h2>
            <p className="text-zinc-500 mt-3 max-w-lg mx-auto text-sm">Ready to get your freight moving? We respond fast.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2 space-y-6" style={{ opacity: contactVisible ? 1 : 0, transform: contactVisible ? "translateX(0)" : "translateX(-30px)", transition: "all 0.7s cubic-bezier(0.16,1,0.3,1) 100ms" }}>
              <div className="border border-zinc-800 bg-[#18181B] rounded-sm p-6 space-y-5 hover-glow">
                <h3 className="font-heading text-lg font-bold text-white">Contact Details</h3>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-sm bg-orange-500/10 flex items-center justify-center flex-shrink-0"><Phone className="w-4 h-4 text-orange-500" /></div>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Phone</p>
                    <a href="tel:0414337803" className="text-sm text-zinc-200 hover:text-orange-400 transition-colors block font-mono" data-testid="contact-phone-hardy">0414 337 803 <span className="text-zinc-500 font-body">(Hardy)</span></a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-sm bg-orange-500/10 flex items-center justify-center flex-shrink-0"><Mail className="w-4 h-4 text-orange-500" /></div>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Email</p>
                    <a href="mailto:info@sghklogistics.com.au" className="text-sm text-zinc-200 hover:text-orange-400 transition-colors break-all block" data-testid="contact-email">info@sghklogistics.com.au</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-sm bg-orange-500/10 flex items-center justify-center flex-shrink-0"><MapPin className="w-4 h-4 text-orange-500" /></div>
                  <div><p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Servicing</p><p className="text-sm text-zinc-200">Australia-Wide</p></div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-sm bg-orange-500/10 flex items-center justify-center flex-shrink-0"><Clock className="w-4 h-4 text-orange-500" /></div>
                  <div><p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Availability</p><p className="text-sm text-zinc-200">24/7 — Call Anytime</p></div>
                </div>
              </div>
              <div className="border border-orange-500/30 bg-orange-500/5 rounded-sm p-6 text-center hover-lift">
                <p className="font-heading text-lg font-bold text-white mb-2">Need Urgent Transport?</p>
                <p className="text-xs text-zinc-400 mb-4">Call us directly for immediate assistance</p>
                <a href="tel:0414337803" className="bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm uppercase tracking-wider px-6 py-3 rounded-[2px] inline-flex items-center gap-2 transition-all hover:shadow-lg hover:shadow-orange-500/20" data-testid="urgent-call-button"><Phone className="w-4 h-4" /> Call Hardy Now</a>
              </div>
            </div>

            <div className="lg:col-span-3" style={{ opacity: contactVisible ? 1 : 0, transform: contactVisible ? "translateX(0)" : "translateX(30px)", transition: "all 0.7s cubic-bezier(0.16,1,0.3,1) 200ms" }}>
              <form onSubmit={handleContact} className="border border-zinc-800 bg-[#18181B] rounded-sm p-6 space-y-4 hover-glow" data-testid="contact-form">
                <h3 className="font-heading text-lg font-bold text-white mb-2">Request a Quote</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Name *</label><Input data-testid="contact-name-input" value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white rounded-[2px] focus:border-orange-500 transition-colors" placeholder="Your name" required /></div>
                  <div><label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Email</label><Input data-testid="contact-email-input" type="email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white rounded-[2px] focus:border-orange-500 transition-colors" placeholder="you@email.com" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Phone</label><Input data-testid="contact-phone-input" value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white rounded-[2px] focus:border-orange-500 transition-colors" placeholder="Your phone" /></div>
                  <div><label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Company</label><Input data-testid="contact-company-input" value={contactForm.company} onChange={e => setContactForm({ ...contactForm, company: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white rounded-[2px] focus:border-orange-500 transition-colors" placeholder="Your company" /></div>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Service Required</label>
                  <select data-testid="contact-service-select" value={contactForm.service} onChange={e => setContactForm({ ...contactForm, service: e.target.value })} className="w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm focus:border-orange-500 transition-colors">
                    <option value="">Select a service...</option>
                    <option value="general_freight">General Freight</option>
                    <option value="oversize_loads">Oversize Loads</option>
                    <option value="container_transport">Container Transport</option>
                    <option value="sidelifter">Sidelifter Operations</option>
                    <option value="interstate_freight">Interstate Freight</option>
                    <option value="construction_haulage">Construction Haulage</option>
                    <option value="machinery_transport">Machinery Transport</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Message *</label>
                  <Textarea data-testid="contact-message-input" value={contactForm.message} onChange={e => setContactForm({ ...contactForm, message: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white rounded-[2px] focus:border-orange-500 transition-colors" rows={4} placeholder="Tell us about your transport needs..." required />
                </div>
                <button data-testid="contact-submit-button" type="submit" disabled={sending} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm uppercase tracking-wider py-3 rounded-[2px] transition-all hover:shadow-lg hover:shadow-orange-500/20 active:scale-[0.98] disabled:opacity-50">
                  {sending ? "Sending..." : "Send Enquiry"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="border-t border-zinc-800 py-12 bg-[#09090B]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <img src={LOGO} alt="SGHK" className="w-10 h-10 object-contain" />
                <div>
                  <span className="font-heading text-lg font-bold text-white">SGHK LOGISTICS</span>
                  <p className="text-[9px] text-zinc-500 uppercase tracking-[0.2em] -mt-0.5">Transport & Haulage</p>
                </div>
              </div>
              <p className="text-sm text-zinc-500 leading-relaxed">Professional transport and logistics services across Australia. Trusted for reliable, safe, and on-time delivery.</p>
            </div>
            <div>
              <h4 className="font-heading text-sm font-bold text-white mb-3 uppercase tracking-wider">Services</h4>
              <ul className="space-y-2 text-sm text-zinc-500">
                <li>General Freight</li><li>Oversize Loads</li><li>Container Transport</li><li>Sidelifter Operations</li><li>Interstate Freight</li><li>Construction Haulage</li><li>Machinery Transport</li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading text-sm font-bold text-white mb-3 uppercase tracking-wider">Contact</h4>
              <ul className="space-y-2 text-sm text-zinc-500">
                <li className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-orange-500" /> <a href="tel:0414337803" className="hover:text-zinc-300 transition-colors">0414 337 803 (Hardy)</a></li>
                <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-orange-500" /> <a href="mailto:info@sghklogistics.com.au" className="hover:text-zinc-300 transition-colors">info@sghklogistics.com.au</a></li>
                <li className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-orange-500" /> Australia-Wide Service</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-zinc-600">&copy; {new Date().getFullYear()} SGHK Logistics. All rights reserved.</p>
            <Link to="/login" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">Team Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
